import { useState, useEffect, useCallback } from 'react'
import { useWedding } from '../../../context/WeddingContext'
import { supabase } from '../../../lib/supabase'
import { compressImage } from '../../../lib/imageCompression'
import { Upload, X, Eye, EyeOff, Camera } from 'lucide-react'

const BUCKET = 'wedding-photos'

export default function PhotosTab() {
  const { wedding , isPremium } = useWedding()
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)

  const FREE_LIMIT = 5
  const count = photos.length

  const fetch = useCallback(async()=>{
    if (!wedding?.id) return
    setLoading(true)
    const {data} = await supabase.from('photos').select('*').eq('wedding_id',wedding.id).order('created_at',{ascending:false})
    setPhotos(data||[]); setLoading(false)
  },[wedding?.id])

  useEffect(()=>{ fetch() },[fetch])

  if (!wedding) return (
    <div style={{ textAlign:'center', padding:'3rem' }}>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC' }}>One moment, pulling up your plans…</p>
    </div>
  )

  async function uploadPhotos(e) {
    const files = Array.from(e.target.files); if(!files.length) return
    const canAdd = isPremium ? files.length : Math.min(files.length, FREE_LIMIT - count)
    if (canAdd <= 0) { alert(`Free plan limit: ${FREE_LIMIT} photos. Upgrade for unlimited.`); return }
    setUploading(true)
    for (const rawFile of files.slice(0, canAdd)) {
      let file = rawFile
      try { file = await compressImage(rawFile) } catch { file = rawFile }   // shrink before upload
      const ext = file.name.split('.').pop()
      const path = `${wedding.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
      const { error } = await supabase.storage.from(BUCKET).upload(path, file)
      if (!error) {
        const { data } = supabase.storage.from(BUCKET).getPublicUrl(path)
        await supabase.from('photos').insert({ wedding_id:wedding.id, public_url:data.publicUrl, storage_path:path, approved:true, is_studio_photo:true, visible_on_portal:true })
      }
    }
    setUploading(false); fetch()
  }

  async function toggleVisibility(photo) {
    const next = !photo.visible_on_portal
    await supabase.from('photos').update({ visible_on_portal:next }).eq('id',photo.id)
    setPhotos(prev=>prev.map(p=>p.id===photo.id?{...p,visible_on_portal:next}:p))
  }

  async function deletePhoto(photo) {
    if (!window.confirm('Delete this photo?')) return
    if (photo.storage_path) await supabase.storage.from(BUCKET).remove([photo.storage_path])
    await supabase.from('photos').delete().eq('id',photo.id)
    setPhotos(prev=>prev.filter(p=>p.id!==photo.id))
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>

      {/* Studio photos header */}
      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:0 }}>
        Upload at least 8 photos — these appear in your guest portal's Studio tab and hero carousel.
      </p>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'0.875rem' }}>
        <div>
          <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', margin:'0 0 0.2rem' }}>Studio Photos</h2>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:0 }}>
            {count} photo{count!==1?'s':''}{!isPremium?` · ${FREE_LIMIT - count} remaining (free)`:''}
          </p>
        </div>
        <label style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'#C4826A', color:'white', borderRadius:100, padding:'0.625rem 1.125rem', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem' }}>
          <Upload size={14}/> {uploading?'Uploading…':'Upload Photos'}
          <input type="file" multiple accept="image/*" style={{ display:'none' }} onChange={uploadPhotos} disabled={uploading}/>
        </label>
      </div>

      {/* Free limit bar */}
      {!isPremium && (
        <div style={{ height:4, background:'#E5E0D8', borderRadius:100 }}>
          <div style={{ height:'100%', width:`${Math.min((count/FREE_LIMIT)*100,100)}%`, background: count>=FREE_LIMIT?'#C4826A':'#9BAF9B', borderRadius:100 }}/>
        </div>
      )}

      {/* Photo grid */}
      {loading ? (
        <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC', textAlign:'center', padding:'2rem' }}>Just a second…</p>
      ) : photos.length===0 ? (
        <div style={{ textAlign:'center', padding:'4rem 1rem' }}>
          <Camera size={36} color="#E5E0D8" style={{ marginBottom:'0.875rem' }}/>
          <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.1rem', margin:'0 0 0.4rem' }}>This is where the beautiful memories live</h3>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#6B6B6B' }}>Upload your pre-wedding and engagement shoot photos. They'll appear on your guest portal and weave through your love story.</p>
        </div>
      ) : (
        <div style={{ columnCount:'auto', columnWidth:160, columnGap:'0.625rem' }}>
          {photos.map(photo=>(
            <div key={photo.id} style={{ breakInside:'avoid', marginBottom:'0.625rem', position:'relative', borderRadius:'0.75rem', overflow:'hidden', opacity:photo.visible_on_portal?1:0.55 }}>
              <img src={photo.public_url} alt="" style={{ width:'100%', display:'block', borderRadius:'0.75rem' }}
                onError={e=>e.target.style.display='none'}/>
              <div style={{ position:'absolute', top:6, right:6, display:'flex', gap:4 }}>
                <button onClick={()=>toggleVisibility(photo)} title={photo.visible_on_portal?'Hide from portal':'Show on portal'}
                  style={{ width:28, height:28, borderRadius:'50%', border:'none', cursor:'pointer', background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {photo.visible_on_portal ? <Eye size={12} color="white"/> : <EyeOff size={12} color="rgba(255,255,255,0.6)"/>}
                </button>
                <button onClick={()=>deletePhoto(photo)}
                  style={{ width:28, height:28, borderRadius:'50%', border:'none', cursor:'pointer', background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <X size={12} color="white"/>
                </button>
              </div>
              {!photo.visible_on_portal && (
                <div style={{ position:'absolute', bottom:6, left:6, background:'rgba(0,0,0,0.55)', borderRadius:100, padding:'2px 8px' }}>
                  <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', color:'rgba(255,255,255,0.7)', fontWeight:600 }}>Hidden</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
